import { first, map } from 'rxjs/operators'
import { createRouter, createWebHistory, RouteRecordRaw, Router } from 'vue-router'

import { ConditionReportState, ConditionReportStatePrefix, WorkspaceType } from '@skinsoft/types'
import { CommonRoutes, ConditionReportRoutes } from '@skinsoft/const'
import { AuthService } from '@skinsoft/auth'
import { Login } from '@skinsoft/auth-ui'
import { ItemEditScreen } from '@skinsoft/entity-ui/src/item/components/item-edit-screen'
import { SoloItemEditScreen } from '@skinsoft/entity-ui/src/item/components/solo-item-edit-screen'
import { ItemEditMenu } from '@skinsoft/entity-ui/src/item/components/item-edit-menu'
import { ActionType } from '@skinsoft/ui'
import { ReportMenu } from './app/component/report-menu'
import { EditReportScreen } from './app/report-edition/edit-report-screen'
import { ReportList } from './app/report-list'
import { ItemSearchScreen, ArchiveSearchScreen, SpecimenSearchScreen } from '@skinsoft/search'
import { ArchiveEditScreen, SpecimenEditScreen } from '@skinsoft/pages'

declare var UNIFY_ROUTER: boolean

const routes = [
    {
        name: ConditionReportRoutes.CONDITION_REPORT_HOME,
        path: '/conditionReportHome',
        redirect: to => {
            const workspaceType = localStorage.getItem("workspaceType")

            if(workspaceType) {
                switch(workspaceType) {
                    case WorkspaceType.archive:
                        return { name: CommonRoutes.ARCHIVES }
                    case WorkspaceType.specimen:
                        return { name: CommonRoutes.SPECIMENS }
                }
            }

            return { name: CommonRoutes.ITEMS }
        },
    },

    {
        component: ArchiveSearchScreen,
        name: CommonRoutes.ARCHIVES,
        path: '/archives',
        props: {
            canCreateObject: true,
            targetRouteName: CommonRoutes.ARCHIVE,
            displayedButtons: [
                /*{
                    type: 'consultLastReport',
                    translation: 'searchFeaturedCard.consultLastReport',
                    class: 'consult-last-report',
                    condition: 'hasLastReportClosed',
                    route: ConditionReportRoutes.CONSULT_REPORT
                },*/
                {
                    type: 'createNewReport',
                    translation: 'searchFeaturedCard.createNewReport',
                    class: 'new-cr-from-scratch',
                    targetRouteName: ConditionReportRoutes.EDIT_REPORT,
                    dataCy: 'archive-featured-card--create-report-btn',
                },
                /*{
                    type: 'createNewReportFromLast',
                    translation: 'searchFeaturedCard.createNewReportFromLast',
                    class: 'new-cr-from-last', 
                    condition: 'hasLastReportNotArchived'
                },*/
            ],
            titleClickAction: {
                action: ActionType.EDIT,
                targetRouteName: CommonRoutes.ARCHIVE
            }
        }
    },
        {
        component: SpecimenSearchScreen,
        name: CommonRoutes.SPECIMENS,
        path: '/specimens',
        props: {
            targetRouteName: ConditionReportRoutes.EDIT_REPORT,
            canCreateObject: true,
            creationTargetRouteName: CommonRoutes.SPECIMEN,
            displayedButtons: [
                // {
                //     type: 'consultLastReport',
                //     translation: 'searchFeaturedCard.consultLastReport',
                //     class: 'consult-last-report',
                //     condition: 'hasLastReportClosed',
                //     route: ConditionReportRoutes.CONSULT_REPORT
                // },
                {
                    type: 'createNewReport',
                    translation: 'searchFeaturedCard.createNewReport',
                    class: 'new-cr-from-scratch',
                    targetRouteName: ConditionReportRoutes.EDIT_REPORT,
                    dataCy: 'create-report-search-featured-card--create-report-btn',
                },
                // {
                //     type: 'createNewReportFromLast',
                //     translation: 'searchFeaturedCard.createNewReportFromLast',
                //     class: 'new-cr-from-last', 
                //     condition: 'hasLastReportNotArchived'
                // },
            ],
            titleClickAction: {
                action: ActionType.EDIT,
                targetRouteName: CommonRoutes.SPECIMEN
            }
        },
    },
    {
        component: ItemSearchScreen,
        name: CommonRoutes.ITEMS,
        path: '/items',
        props: {
            targetRouteName: ConditionReportRoutes.EDIT_REPORT,
            canCreateObject: true,
            creationTargetRouteName: CommonRoutes.ITEM,
            displayedButtons: [
                {
                    type: 'consultLastReport',
                    translation: 'searchFeaturedCard.consultLastReport',
                    class: 'consult-last-report',
                    condition: 'hasLastReportClosed',
                    route: ConditionReportRoutes.CONSULT_REPORT
                },
                {
                    type: 'createNewReport',
                    translation: 'searchFeaturedCard.createNewReport',
                    class: 'new-cr-from-scratch',
                    dataCy: 'create-report-search-featured-card--create-report-btn',
                },
                {
                    type: 'createNewReportFromLast',
                    translation: 'searchFeaturedCard.createNewReportFromLast',
                    class: 'new-cr-from-last', 
                    condition: 'hasLastReportNotArchived'
                },
            ],
            titleClickAction: {
                action: ActionType.EDIT,
                targetRouteName: CommonRoutes.ITEM
            }
        },
    },
    {
        components: {
            actionMenu: ItemEditMenu,
            default: SoloItemEditScreen,
        },
        name: CommonRoutes.ITEM,
        path: '/item/:itemId',
        props: {
            default: route => ({
                itemId: route.params.itemId,
                itemEditScreen: ItemEditScreen,
                routes: {
                    offlineRoute: ConditionReportRoutes.CONDITION_REPORT_HOME,
                    onlineRoute: ConditionReportRoutes.CONDITION_REPORT_HOME
                }
            }),
        }
    },
    {
        components: {
            actionMenu: ItemEditMenu,
            default: ArchiveEditScreen,
        },
        name: CommonRoutes.ARCHIVE,
        path: '/archive/:archiveId',
        props: {
            default: route => ({
                archiveId: route.params.archiveId,
                navigateBackRoute: ConditionReportRoutes.CONDITION_REPORT_HOME,
            }),
        }
    },
    {
        components: {
            actionMenu: ItemEditMenu,
            default: SpecimenEditScreen,
        },
        name: CommonRoutes.SPECIMEN,
        path: '/specimen/:specimenId',
        props: {
            default: route => ({
                specimenId: route.params.specimenId,
                navigateBackRoute: ConditionReportRoutes.CONDITION_REPORT_HOME,
            }),
        }
    },
    {
        components: {
            actionMenu: ReportMenu,
            default: EditReportScreen,
        },
        props: {
            actionMenu: true,
            default: {
                navigateBackRoute: ConditionReportRoutes.CURRENT_REPORTS,
            },
        },
        name: ConditionReportRoutes.EDIT_REPORT,
        path: '/edit-report/:reportId',
    },
    {
        component: ReportList,
        name: ConditionReportRoutes.CURRENT_REPORTS,
        path: '/current-reports',
        props: {
            listState: ConditionReportStatePrefix + ConditionReportState.IN_PROGRESS,
            targetRouteName: ConditionReportRoutes.EDIT_REPORT,
            titleI18NKey: 'listReportScreen.inProgress.title',
            titleClickAction: {
                action: ActionType.EDIT,
                targetRouteName: ConditionReportRoutes.EDIT_REPORT
            },
        },
    },
    {
        components: {
            actionMenu: ReportMenu,
            default: EditReportScreen,
        },
        props: {
            actionMenu:route => ({
                reportId: route.params.reportId,
                readOnly: true,
            }),
            default: {
                readOnly: true,
                navigateBackRoute: ConditionReportRoutes.CLOSE_REPORTS,
            },
        },
        name: ConditionReportRoutes.CONSULT_REPORT,
        path: '/consult-report/:reportId',
    },
    {
        component: ReportList,
        name: ConditionReportRoutes.CLOSE_REPORTS,
        path: '/close-reports',
        props: {
            listState: ConditionReportStatePrefix + ConditionReportState.CLOSED,
            targetRouteName: "",
            titleI18NKey: 'listReportScreen.closed.title',
            titleClickAction: {
                action: ActionType.EDIT,
                targetRouteName: ConditionReportRoutes.CONSULT_REPORT
            },
        },
    },
    { path: '/:pathMatch(.*)*', redirect: '/conditionReportHome' }
]

// tslint:disable-next-line:variable-name
let _router: Router;

// If app is embedded in launcher, use launcher router
if (UNIFY_ROUTER) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    _router = require('../../../reporter-launcher/src/router').router
}
// If app is standalone, use its own router
else {
    _router = createRouter({
        history: createWebHistory(),
        routes: [],
    });
    _router.addRoute({
        component: Login,
        name: CommonRoutes.LOGIN,
        props: {
            homeRoute: ConditionReportRoutes.CONDITION_REPORT_HOME
        },
        path: '/login',
    })
}
// cast: vue-router 4 infers this array as a union of record literals, not RouteRecordRaw[].
routes.forEach(route => _router.addRoute(route as RouteRecordRaw))

export const router = _router;

router.beforeEach((to, __, next) => {
    AuthService.getInstance()
        .waitForUser$()
        .pipe(
            first(),
            map(authenticated => {
                if (!(authenticated && authenticated.user) && to.name !== CommonRoutes.LOGIN) {
                    return { name: CommonRoutes.LOGIN };
                }

                if (authenticated && authenticated.user && to.name === CommonRoutes.LOGIN) {
                    return false;
                }

                return;
            }),
        )
        .subscribe(next);
});
